import { useCallback, useEffect, useState } from "react";
import type { TestPatientState } from "@care-by-ella/contracts";
import {
  fetchTestEvents,
  fetchTestState,
  simulateEvent,
} from "@/lib/test-mode-api";
import { useReadOnly } from "./SupportView";

// TES-298 — the simulate & observe panel, rendered as the "Test" tab on a
// TEST patient's detail page. Left: one button per catalog event. Right:
// what happened — domain events, triggered flows, scheduled notifications,
// chat drafts awaiting approval. Dispatch is post-commit, so the state
// refetches on a short delay after each simulation.

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

export function TestPatientPanel({ patientId }: { patientId: string }) {
  // TES-1040 — the admin's read-only view observes; it does not simulate.
  const readOnly = useReadOnly();
  const [events, setEvents] = useState<string[]>([]);
  const [state, setState] = useState<TestPatientState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firing, setFiring] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchTestState(patientId)
      .then(setState)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [patientId]);

  useEffect(() => {
    if (!readOnly) {
      fetchTestEvents()
        .then((r) => setEvents([...r.events]))
        .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    }
    load();
  }, [load, readOnly]);

  const fire = async (event: string) => {
    setFiring(event);
    setError(null);
    try {
      await simulateEvent(patientId, event);
      // Flow dispatch runs after the request's tx commits — give it a beat.
      setTimeout(load, 600);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFiring(null);
    }
  };

  return (
    <section className="flex flex-col gap-lg">
      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {readOnly ? null : (
        <div className="bg-surface rounded-md border border-surfaceMuted p-md">
          <h2 className="font-display text-lg mb-xs">Simulera händelse</h2>
          <p className="text-xs text-textSecondary mb-md">
            Avfyrar händelsen genom den riktiga eventmotorn (persisterad
            händelse, flödestriggrar, autoslutförande). Utskick till
            testpatienter går aldrig till riktiga kanaler.
          </p>
          <div className="flex flex-wrap gap-sm">
            {events.map((e) => (
              <button
                key={e}
                type="button"
                disabled={firing !== null}
                onClick={() => void fire(e)}
                className="px-md py-xs rounded-md border border-surfaceMuted text-sm hover:border-brand hover:text-brand transition disabled:opacity-50"
              >
                {firing === e ? "Avfyrar…" : e}
              </button>
            ))}
          </div>
        </div>
      )}

      {state ? (
        <>
          <div className="bg-surface rounded-md border border-surfaceMuted p-md">
            <div className="flex items-center justify-between mb-sm">
              <h2 className="font-display text-lg">Händelselogg</h2>
              <button
                type="button"
                onClick={load}
                className="text-xs text-brand underline"
              >
                Uppdatera
              </button>
            </div>
            <p className="text-xs text-textSecondary mb-sm">
              Journey state:{" "}
              <span className="font-mono">{state.journeyState}</span>
            </p>
            {state.events.length === 0 ? (
              <p className="text-sm text-textSecondary">Inga händelser ännu.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-textSecondary">
                  <tr>
                    <th className="pb-xs">Händelse</th>
                    <th className="pb-xs">Källa</th>
                    <th className="pb-xs">Tid</th>
                    <th className="pb-xs">Dispatch</th>
                  </tr>
                </thead>
                <tbody>
                  {state.events.map((e) => (
                    <tr key={e.id} className="border-t border-surfaceMuted">
                      <td className="py-xs font-mono text-xs">{e.eventKey}</td>
                      <td className="py-xs">{e.source}</td>
                      <td className="py-xs">{fmt(e.occurredAt)}</td>
                      <td className="py-xs">
                        {e.dispatchedAt ? "✓" : "väntar"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-surface rounded-md border border-surfaceMuted p-md">
            <h2 className="font-display text-lg mb-sm">Aktiva flöden</h2>
            {state.flows.assignments.length === 0 ? (
              <p className="text-sm text-textSecondary">Inga aktiva flöden.</p>
            ) : (
              <ul className="flex flex-col gap-xs text-sm">
                {state.flows.assignments.map((f) => (
                  <li key={f.id} className="flex gap-sm items-center">
                    <span className="font-medium">{f.flowName}</span>
                    <span className="text-xs text-textSecondary">
                      v{f.flowVersion} · {f.status} · {f.source}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
            <div className="bg-surface rounded-md border border-surfaceMuted p-md">
              <h2 className="font-display text-lg mb-sm">
                Schemalagda notiser
              </h2>
              {state.scheduledNotifications.length === 0 ? (
                <p className="text-sm text-textSecondary">Inga notiser.</p>
              ) : (
                <ul className="flex flex-col gap-xs text-xs">
                  {state.scheduledNotifications.map((n) => (
                    <li key={n.id} className="flex justify-between gap-sm">
                      <span className="font-mono">{n.type}</span>
                      <span className="text-textSecondary">
                        {n.status} · {fmt(n.fireAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-surface rounded-md border border-surfaceMuted p-md">
              <h2 className="font-display text-lg mb-sm">Chattutkast</h2>
              {state.chatDrafts.length === 0 ? (
                <p className="text-sm text-textSecondary">Inga utkast.</p>
              ) : (
                <ul className="flex flex-col gap-xs text-xs">
                  {state.chatDrafts.map((d) => (
                    <li key={d.id}>
                      <span className="text-textSecondary">
                        {d.attribution} · {d.status} · {fmt(d.createdAt)}
                      </span>
                      <p className="truncate">{d.body}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
